import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlags } from '@funtech-int/yango-shared';
import { playerBaseInfoQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { useOnGraphQLError } from '@/scenarios/legacy-player/components/player-manager/hooks/use-on-graphql-error';
import { ContentType } from '@/scenarios/legacy-player/components/player-manager/types';
import { setReadyToPlay } from '@/scenarios/legacy-player/components/player-manager/hooks/use-is-ready-to-play';
import { Source } from '@/scenarios/legacy-player/types/config';
import { useGraphqlClient } from '@/shared/hooks/useGraphqlClient';

import { getPsid } from '../../../Player/lib/utils/psid';
import { usePlayerContentPrepared } from '../../analytics/hooks/use-player-content-prepared';
import { getPlayerType } from '../../utils/config/get-player-type';
import { SupportedStreamFormatHeader } from '../../constants/supported-streams-headers';
import { PlayerBaseInfoContent } from '../../../player-manager/context/PlayerBaseInfoProvider';
import { featureFlagsScheme } from '../../../../lib/featureFlags';

import { useBaseConfig } from './use-base-config';

interface UsePlayerConfigParams {
    streamHandler: (baseInfo: PlayerBaseInfoContent) => Source[];
    onError?: (error: Error) => void;
}

function isAbortError(value: unknown): value is DOMException {
    return value instanceof DOMException && value.name === 'AbortError';
}

export const usePlayerConfig = ({
    streamHandler,
    onError: onCustomError,
}: UsePlayerConfigParams) => {
    const { flags } = useFeatureFlags(featureFlagsScheme);
    const graphqlClient = useGraphqlClient();
    const { onError, onGraphQLError } = useOnGraphQLError();

    const [isLoading, setIsLoading] = useState(true);
    const [contentType, setContentType] = useState<ContentType | undefined>(undefined);
    const [playerBaseInfoContent, setPlayerBaseInfoContent] =
        useState<PlayerBaseInfoContent | null>(null);

    const trackedPlayerContentPrepared = usePlayerContentPrepared();

    const prerollsSupported = flags.get('isDynamicPrerollEnabled');

    const inFlightPlayerBaseInfoByKey = useRef(new Map<string, Promise<Source | Source[]>>());
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(
        () => () => {
            abortControllerRef.current?.abort();
        },
        []
    );

    const config = useBaseConfig({
        getSource: useCallback(
            ({ contentId, vsid }) => {
                const requestKey = `${contentId}:${vsid}:${String(prerollsSupported)}`;
                const inFlight = inFlightPlayerBaseInfoByKey.current.get(requestKey);
                if (inFlight) {
                    return inFlight;
                }

                setIsLoading(true);
                setContentType(undefined);
                setPlayerBaseInfoContent(null);

                abortControllerRef.current?.abort();
                const controller = new AbortController();
                abortControllerRef.current = controller;

                const requestPromise = graphqlClient
                    .fetch(
                        {
                            operation: playerBaseInfoQuery,
                            variables: { contentId, prerollsSupported },
                        },
                        {
                            headers: {
                                ...SupportedStreamFormatHeader,
                                'X-Application-VSID': vsid,
                            },
                            abortSignal: controller.signal,
                        }
                    )
                    .then(({ data, errors }) => {
                        setIsLoading(false);
                        if (controller.signal.aborted) {
                            return [];
                        }
                        if (errors && errors.length > 0 && errors[0]) {
                            setPlayerBaseInfoContent(null);
                            onGraphQLError(errors[0]);
                            return [];
                        }
                        if (data) {
                            setPlayerBaseInfoContent(data);
                            setReadyToPlay(true);
                            trackedPlayerContentPrepared({
                                vsid,
                                isDynamicPrerollEnabled: prerollsSupported,
                                psid: getPsid(),
                                manifestUrl: '',
                                contentId,
                                content: data,
                            });
                            setContentType(data.__typename);
                            return streamHandler(data);
                        }
                        setPlayerBaseInfoContent(null);
                        return [];
                    })
                    .catch((error) => {
                        if (controller.signal.aborted || isAbortError(error)) {
                            return [];
                        }
                        setIsLoading(false);
                        setPlayerBaseInfoContent(null);
                        onError(error);
                        return [];
                    })
                    .finally(() => {
                        inFlightPlayerBaseInfoByKey.current.delete(requestKey);
                    });

                inFlightPlayerBaseInfoByKey.current.set(requestKey, requestPromise);

                return requestPromise;
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [onCustomError, prerollsSupported, streamHandler]
        ),
    });

    return {
        config,
        isLoading,
        contentType,
        playerType: getPlayerType(contentType),
        playerBaseInfoContent,
    };
};
